import { Controller } from "@hotwired/stimulus";

/**
 * api-sync (Stimulus controller)
 *
 * But:
 * - observe 1+ formulaires dans un conteneur
 * - construire un payload JSON "nested" (style Rails) à partir des champs
 * - POST debounced vers une URL (par scope) et émettre des events pour que le rendu se fasse ailleurs
 *
 * Scopes / pipelines
 * - Mode multi-scope (par défaut): chaque <form data-api-sync-form data-api-sync-scope="a,b">
 *   contribue aux pipelines "a" et "b"
 * - Mode single-scope (legacy): data-api-sync-scope-value="a" sur le controller → un seul pipeline "a"
 *
 * Évènements émis (bubble sur les "event sinks"):
 * - remote:update:start   (detail: { payload, channel })
 * - remote:update:success (detail: { json, payload, channel })
 * - remote:update:error   (detail: { err, payload, channel })
 * - remote:update:finish  (detail: { payload, channel })
 *
 * Event sinks (cibles de dispatch):
 * - Priorité: éléments avec data-api-sync-event-sink="listing_details,listings_search"
 * - Sinon: data-api-sync-target="eventSink" (legacy) ou this.element
 *
 * URL de l'API:
 * - data-api-sync-url-value sur le controller, ou bien sur un form (optionnellement scoppé via data-api-sync-url-scope)
 *
 * Pipelines suiveurs:
 * - data-api-sync-follow-scope="listing_details" sur un form: son pipeline est
 *   rafraîchi (même patch) à chaque "api-sync:refresh" visant ce scope
 *
 *
 * Spinners:
 * - Dans les event sinks, tout élément [data-api-sync-target="spinner"] sera togglé.
 *
 * Important:
 * - Les champs marqués [data-api-sync-adjustable] suivent une logique "dirty" (voir buildPayload)
 * - Les champs marqués [data-api-sync-force-submit] sont toujours inclus dans
 *   le payload, même hidden ou disabled. Mécanisme indépendant de la logique
 *   adjustable/apply-from/dirty. Utilisé pour les champs masqués selon la
 *   fiscalité active (loyers, pack meublé, frais de rénovation…) afin que le
 *   listing virtuel reconstruit côté backend soit invariant par rapport au
 *   tax_program affiché dans l'UI.
 * - Les tableaux Rails "a[b][]" sont supportés (FormData émet plusieurs entrées).
 */

// Évènements de saisie écoutés par délégation sur l'élément du contrôleur.
// `blur` ne bulle pas : les écouteurs sont posés en phase de capture.
const DELEGATED_INPUT_EVENTS = ["input", "change", "blur"];

export default class extends Controller {
  static targets = ["eventSink"];
  static values = {
    url: String,
    debounce: { type: Number, default: 300 },
    // Legacy single-scope mode (optional): when provided, only forms matching this scope are used.
    scope: String,
    // Scopes (séparés par des virgules) dont on saute le fetch initial au
    // connect : un autre contrôleur les déclenchera lui-même. Ex. en mode
    // catalogue, on diffère "listing_details" car l'auto-sélection du premier
    // bien le déclenchera après la première recherche (sinon un get_details
    // "Simulation libre" part au chargement puis est aussitôt écrasé).
    deferInitialScopes: String,
  };

  connect() {
    // ---------- Init ----------
    // Backward compatible:
    // - if scopeValue is provided, behaves like "single pipeline"
    // - otherwise, supports multiple pipelines, keyed by data-api-sync-scope on forms
    this.singleScope = (this.hasScopeValue && this.scopeValue) ? String(this.scopeValue) : null;

    this.allForms = [];
    this.pipelines = new Map(); // scope -> state
    this.syncForms();

    // Délégation sur `this.element` plutôt qu'un écouteur par formulaire : des
    // sous-arbres du conteneur sont remplacés à chaud (ex. la colonne détails
    // restaurée par `listing-results#restoreDetailsColumnInitialState`), ce qui
    // recrée les formulaires qu'ils contiennent. Des écouteurs posés sur chaque
    // <form> resteraient attachés aux nœuds détachés et la saisie dans le
    // formulaire neuf passerait inaperçue (cf. #662).
    this.onAnyInput = (event) => this.handleAnyInput(event);
    DELEGATED_INPUT_EVENTS.forEach((ev) => {
      this.element.addEventListener(ev, this.onAnyInput, true);
    });

    // ---------- Manual refresh ----------
    // Manual refresh
    this.onRefresh = (e) => {
      const scope = (e && e.detail && e.detail.scope) ? String(e.detail.scope) : this.singleScope;
      if (!scope) return;
      const force = Boolean(e && e.detail && e.detail.force);
      const payloadPatch = (e && e.detail) ? e.detail.payloadPatch : null;
      this.refresh(scope, { force, payloadPatch });
      // Les pipelines suiveurs se rafraîchissent avec le même patch : un
      // `api-sync:refresh` émis par un autre contrôleur ne connaît qu'un seul
      // scope (historiquement "listing_details"), alors que plusieurs blocs
      // peuvent dépendre du même changement.
      this.followersOf(scope).forEach((follower) => {
        this.refresh(follower, { force, payloadPatch });
      });
    };
    this.element.addEventListener("api-sync:refresh", this.onRefresh);

    // ---------- Clear sticky patch ----------
    // Allows external controllers to clear the fiscality patch (e.g., when returning to list)
    this.onClearStickyPatch = (e) => {
      const scope = e?.detail?.scope;
      if (!scope) return;
      this.clearStickyPatch(scope);
    };
    this.element.addEventListener("api-sync:clear-sticky-patch", this.onClearStickyPatch);

    // ---------- Initial fetch ----------
    // Initial fetch for all scopes, sauf ceux explicitement différés.
    const deferred = (this.hasDeferInitialScopesValue ? this.deferInitialScopesValue : "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    if (this.singleScope) {
      if (!deferred.includes(this.singleScope)) this.scheduleUpdate(this.singleScope);
    } else {
      Array.from(this.pipelines.keys()).forEach((scope) => {
        if (!deferred.includes(scope)) this.scheduleUpdate(scope);
      });
    }
  }

  disconnect() {
    // ---------- Cleanup ----------
    DELEGATED_INPUT_EVENTS.forEach((ev) => {
      try { this.element.removeEventListener(ev, this.onAnyInput, true); } catch (_) {}
    });
    try { this.element.removeEventListener("api-sync:refresh", this.onRefresh); } catch (_) {}
    try { this.element.removeEventListener("api-sync:clear-sticky-patch", this.onClearStickyPatch); } catch (_) {}

    // cancel all pipelines
    this.pipelines.forEach((p) => {
      try { if (p.abortController) p.abortController.abort(); } catch (_) {}
      if (p.debounceTimer) clearTimeout(p.debounceTimer);
    });
  }

  // ------- Input handling and dirty/reset flags -------
  handleAnyInput(event) {
    const target = event && event.target;
    // Écouteur délégué : seuls les champs d'un formulaire api-sync comptent.
    const form = (target && target.closest) ? target.closest("form[data-api-sync-form]") : null;
    if (!form) return;
    // Ignore synthetic events dispatched by application of API response
    if (event.__investDetailsFromApply || (event.detail && event.detail.fromInvestApply)) {
      return;
    }
    // Ignore elements explicitly marked
    if (target.closest("[data-api-sync-ignore]")) return;

    // Le formulaire peut être neuf (sous-arbre remplacé) : on resynchronise les
    // pipelines avant de toucher aux drapeaux `dirty` et de programmer l'update.
    this.syncForms();

    try {
      // Mark adjustable fields as dirty on user input
      if (target.hasAttribute && target.hasAttribute("data-api-sync-adjustable")) {
        target.dataset.apiSyncDirty = "true";
      }
      // Reset dirty for specified fields when a reset trigger changes
      if (target.hasAttribute && target.hasAttribute("data-api-sync-reset-dirty-for")) {
        const list = (target.getAttribute("data-api-sync-reset-dirty-for") || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        const scopes = this.scopesOf(form);
        scopes.forEach((scope) => {
          const pipe = this.pipelines.get(scope);
          if (!pipe) return;
          list.forEach((name) => {
            const el = pipe.adjustablesByName[name];
            if (el) {
              el.dataset.apiSyncDirty = "false";
              pipe.resetApplySet.add(name);
            }
          });
        });
      }
    } catch (_) {}

    // Trigger update for relevant scope(s)
    this.scopesOf(form).forEach((s) => this.scheduleUpdate(s));
  }

  /**
   * Pipelines alimentés par un formulaire (mode legacy single-scope inclus).
   * @param {Element|null} form
   * @returns {string[]}
   */
  scopesOf(form) {
    if (this.singleScope) return [this.singleScope];
    return this.parseScopes(form && form.getAttribute("data-api-sync-scope"));
  }

  // ------- Scheduling and network -------
  /**
   * Programme un update debounced pour le pipeline.
   * @param {string} scope
   */
  scheduleUpdate(scope) {
    const pipe = this.pipelines.get(scope);
    if (!pipe) return;
    if (pipe.debounceTimer) clearTimeout(pipe.debounceTimer);
    const delay = typeof this.debounceValue === "number" ? this.debounceValue : 300;
    // On remet `debounceTimer` à null quand il se déclenche pour que l'état
    // "au repos" du pipeline (cf. `apiSyncBusy`) soit fiable : sans ça un
    // timer déjà exécuté resterait référencé et fausserait la détection.
    pipe.debounceTimer = setTimeout(() => {
      pipe.debounceTimer = null;
      this.updateFromApi(scope);
    }, delay);
  }

  /**
   * Indique si au moins un pipeline a du travail en cours : un update debounced
   * programmé (en attente) OU un fetch en vol. Utilisé par les tests
   * (`wait_for_ajax`/`wait_for_api_sync`) pour attendre de façon déterministe la
   * fin des cycles api-sync (qui passent par `fetch` et non jQuery), évitant la
   * flakiness des specs feature qui lisent des champs calculés (#227, etc.).
   * @returns {boolean}
   */
  apiSyncBusy() {
    if (!this.pipelines) return false;
    let busy = false;
    this.pipelines.forEach((pipe) => {
      if (pipe.debounceTimer != null) busy = true;
      if ((pipe.inFlight || 0) > 0) busy = true;
    });
    return busy;
  }

  /**
   * Force un refresh immédiat.
   * @param {string} scope
   * @param {{force?: boolean, payloadPatch?: object|null}} opts
   */
  refresh(scope, { force, payloadPatch } = {}) {
    this.syncForms();
    const pipe = this.pipelines.get(scope);
    if (pipe) {
      // Clear any pending debounce timer to prevent it from overriding this refresh
      if (pipe.debounceTimer) {
        clearTimeout(pipe.debounceTimer);
        pipe.debounceTimer = null;
      }
      // Store the payloadPatch as "sticky" so subsequent requests also apply it
      // until a request succeeds with this patch
      if (payloadPatch) {
        pipe.stickyPayloadPatch = payloadPatch;
      }
    }
    this.updateFromApi(scope, { force: Boolean(force), payloadPatch });
  }

  /**
   * Stimulus action: clear the fiscality sticky patch for listing_details.
   * Called when the user changes the fiscality via the profile select.
   * This ensures the comparator fiscality override is reset.
   */
  clearFiscalityPatch(event) {
    this.clearStickyPatch("listing_details");
  }

  /**
   * Efface le patch sticky d'un pipeline **et de ses suiveurs**.
   *
   * `onRefresh` recopie le `payloadPatch` sur chaque suiveur, qui le rend donc
   * sticky lui aussi : ne nettoyer que le scope visé laisserait un suiveur
   * (ex. `new_vs_old`, suiveur de `listing_details`) figé sur la fiscalité
   * choisie dans le comparateur, même après un retour explicite à la fiscalité
   * du profil. Cf. docs/fiscality_selector.md.
   * @param {string} scope
   */
  clearStickyPatch(scope) {
    if (!scope) return;
    [scope, ...this.followersOf(scope)].forEach((pipeScope) => {
      const pipe = this.pipelines.get(pipeScope);
      if (pipe) pipe.stickyPayloadPatch = null;
    });
  }

  /**
   * Construit le payload du pipeline + POST vers l'API.
   * @param {string} scope
   * @param {{force?: boolean, payloadPatch?: object|null}} opts
   */
  updateFromApi(scope, { force, payloadPatch } = {}) {
    // Le payload doit être construit sur les formulaires présents dans le DOM
    // au moment de l'envoi, pas sur ceux connus au `connect()` (#662).
    this.syncForms();
    const pipe = this.pipelines.get(scope);
    if (!pipe) return;

    const apiUrl = this.resolveApiUrl(scope);
    if (!apiUrl) return;

    const basePayload = this.buildPayload(scope);
    // Apply sticky payloadPatch if no explicit patch is provided
    const effectivePatch = payloadPatch || pipe.stickyPayloadPatch || null;
    const payload = effectivePatch ? this.applyPayloadPatch(basePayload, effectivePatch) : basePayload;
    const body = JSON.stringify(payload);
    if (!force && pipe.lastRequestBody === body) {
      // Dedupe : on ne saute le fetch que si la dernière réponse réellement
      // appliquée correspond exactement à ce body. Sinon (réponse précédente
      // annulée par AbortController, ou réponse "stale" appliquée entre-temps),
      // l'UI peut être désynchronisée et on doit forcer un re-fetch pour
      // garantir la cohérence des champs calculés (cf. issue #227 : montant
      // d'impôt qui reste bloqué à 0 € après saisies rapides sur les salaires).
      if (pipe.lastSuccessfulBody === body) return;
    }
    pipe.lastRequestBody = body;
    const sentBody = body;

    const currentId = ++pipe.requestId;
    this.cancelInFlight(scope);
    pipe.abortController = new AbortController();

    this.showSpinner(true, scope);
    this.emitRemoteEvent(scope, "start", { payload, channel: scope });

    const headers = { "Content-Type": "application/json", Accept: "application/json" };
    const apiToken = this.getApiToken();
    if (apiToken) {
      headers["Authorization"] = `Bearer ${apiToken}`;
    }

    // Marque le pipeline comme "occupé" pour la durée du fetch (cf. apiSyncBusy).
    pipe.inFlight = (pipe.inFlight || 0) + 1;

    fetch(apiUrl, {
      method: "POST",
      headers,
      body,
      signal: pipe.abortController.signal,
    })
      .then((r) => (r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`))))
      .then((json) => {
        if (currentId !== pipe.requestId) return;
        // Clear sticky patch on successful request, EXCEPT for fiscality patches
        // (comparateur de fiscalité) which should persist until explicit reset.
        // See docs/fiscality_selector.md for rationale.
        if (!this.isFiscalityPatch(pipe.stickyPayloadPatch)) {
          pipe.stickyPayloadPatch = null;
        }
        // Mémorise le body et le JSON de la dernière réponse réellement
        // appliquée, utilisé par la logique de dedupe pour détecter une
        // désynchronisation entre l'état du formulaire et l'UI (issue #227).
        pipe.lastSuccessfulBody = sentBody;
        pipe.lastSuccessJson = json;
        this.emitRemoteEvent(scope, "success", { json, payload, channel: scope });
        // Also apply suggestions to inputs when requested (legacy compatibility)
        this.applyInputSuggestions(scope, json);
      })
      .catch((err) => {
        if (err.name === "AbortError") return;
        // eslint-disable-next-line no-console
        console.warn("api-sync fetch error:", err);
        pipe.lastRequestBody = null; // allow retry even if payload unchanged
        this.emitRemoteEvent(scope, "error", { err, payload, channel: scope });
      })
      .finally(() => {
        // Décrément inconditionnel : le `finally` s'exécute aussi sur abort,
        // afin que `inFlight` reste équilibré avec l'incrément pré-fetch.
        pipe.inFlight = Math.max(0, (pipe.inFlight || 0) - 1);
        if (currentId === pipe.requestId) {
          this.showSpinner(false, scope);
          this.emitRemoteEvent(scope, "finish", { payload, channel: scope });
        }
      });
  }

  /**
   * Émet remote:update:* sur les sinks résolus.
   * @param {string} scope
   * @param {"start"|"success"|"error"|"finish"} kind
   * @param {object} detail
   */
  emitRemoteEvent(scope, kind, detail = {}) {
    const evName = `remote:update:${kind}`;
    const targets = this.resolveEventSinks(scope);
    targets.forEach((target) => {
      try {
        target.dispatchEvent(new CustomEvent(evName, { bubbles: true, detail }));
      } catch (_) {}
    });
  }

  resolveEventSinks(scope) {
    // New: use data-api-sync-event-sink="listing_details,listings_search"
    const sinks = Array.from(this.element.querySelectorAll("[data-api-sync-event-sink]")).filter((el) => {
      const raw = el.getAttribute("data-api-sync-event-sink") || "";
      const scopes = this.parseScopes(raw);
      return scopes.includes(scope);
    });
    if (sinks.length > 0) return sinks;
    // Legacy targets
    if (this.hasEventSinkTarget) return this.eventSinkTargets;
    return [this.element];
  }

  /**
   * Résout l'URL de l'API pour un scope.
   * @param {string} scope
   * @returns {string|null}
   */
  resolveApiUrl(scope) {
    // New value API on controller
    if (this.hasUrlValue && this.urlValue) return this.urlValue;
    // Or on forms/element via data-api-sync-url-value
    const pipe = this.pipelines.get(scope);
    const forms = pipe ? pipe.forms : [];
    // Prefer an URL explicitly scoped to this pipeline
    for (const form of forms) {
      const raw = form.getAttribute("data-api-sync-url-scope") || "";
      const scopes = this.parseScopes(raw);
      const v = form.getAttribute("data-api-sync-url-value");
      if (v && scopes.includes(scope)) return v;
    }
    for (const form of forms) {
      const v = form.getAttribute("data-api-sync-url-value");
      if (v) return v;
    }
    const onContainer = this.element.getAttribute("data-api-sync-url-value");
    return onContainer || null;
  }

  // ------- Payload -------
  /**
   * Merge tous les forms du scope en un payload nested.
   * Applique ensuite:
   * - overwriteWithNumberFormatRaw
   * - logique dirty des [data-api-sync-adjustable]
   */
  buildPayload(scope) {
    const pipe = this.pipelines.get(scope);
    if (!pipe) return {};
    const merged = {};
    for (const form of pipe.forms) {
      const obj = this.formToNestedObject(form);
      this.deepMerge(merged, obj);
    }
    // IMPORTANT: number-format controller duplicates fields by adding a hidden input
    // with the same name. In some cases, that hidden input can carry a stale value
    // and override the visible value in FormData iteration order.
    // To ensure we always send the current raw numeric value, we overwrite merged
    // values from any enabled input using number-format dataset.raw.
    this.overwriteWithNumberFormatRaw(merged, pipe.forms);
    Object.entries(pipe.adjustablesByName).forEach(([name, el]) => {
      const isDirty = (el.dataset.apiSyncDirty || "").toLowerCase() === "true";
      if (!isDirty) {
        this.deleteByName(merged, name);
      }
    });
    this.includeForceSubmitValues(merged, pipe.forms);
    return merged;
  }

  // Inclut dans le payload tous les inputs marqués [data-api-sync-force-submit]
  // (typiquement masqués via toggle selon la fiscalité active), pour garantir
  // que le backend reçoit toujours leur valeur réelle. Indépendant de la
  // logique dirty/adjustable.
  includeForceSubmitValues(merged, forms) {
    try {
      const els = [];
      (forms || []).forEach((form) => {
        try {
          els.push(...Array.from(form.querySelectorAll("[data-api-sync-force-submit]")));
        } catch (_) {}
      });
      els.forEach((el) => {
        try {
          if (!el || !el.getAttribute) return;
          const name = el.getAttribute("name");
          if (!name) return;
          // Pour une checkbox/radio non cochée, ne pas forcer sa valeur (qui
          // vaut toujours "1" sur l'élément) : on laisse le hidden companion
          // fournir "0" via FormData (cf. interrupteur TVA réduite #388).
          if ((el.type === "checkbox" || el.type === "radio") && !el.checked) return;
          const raw = el.dataset ? el.dataset.raw : null;
          const value = (raw != null && raw !== "") ? raw : el.value;
          if (value == null || value === "") return;
          this.assignNested(merged, name, value);
        } catch (_) {
          // no-op
        }
      });
    } catch (_) {
      // no-op
    }
  }

  overwriteWithNumberFormatRaw(merged, forms) {
    try {
      const inputs = [];
      (forms || []).forEach((form) => {
        try {
          inputs.push(...Array.from(form.querySelectorAll("input[data-controller~='number-format']")));
        } catch (_) {}
      });
      inputs.forEach((input) => {
        try {
          if (!input || !input.getAttribute) return;
          if (input.disabled) return;
          const name = input.getAttribute("name");
          if (!name) return;
          const raw = input.dataset ? input.dataset.raw : null;
          if (raw == null || raw === "") return;
          // dataset.raw is already normalized by number-format
          this.assignNested(merged, name, raw);
        } catch (_) {
          // no-op
        }
      });
    } catch (_) {
      // no-op
    }
  }

  // ------- Suggestions application (legacy compatibility) -------
  //
  // Sur chaque succès api-sync on (ré)applique :
  // - tous les champs présents dans `resetApplySet` (déclencheurs `reset-dirty-for`)
  // - en plus, tous les champs adjustables dont `data-api-sync-apply-from`
  //   pointe vers `calculated_values.*` ET qui ne sont pas marqués dirty.
  //
  // Cette double application découple les champs calculés de `resetApplySet`
  // (cf. issue #227). Sans ça, si une réponse "stale" arrivait et vidait
  // `resetApplySet` avant que la bonne réponse n'arrive (ou si la bonne
  // réponse était dédupliquée), un champ comme « Montant de l'impôt »
  // pouvait rester bloqué sur une valeur incohérente avec les saisies.
  applyInputSuggestions(scope, json) {
    // Un sous-arbre peut avoir été remplacé pendant le vol de la requête : les
    // suggestions doivent atterrir dans les champs vivants.
    this.syncForms();
    const pipe = this.pipelines.get(scope);
    if (!pipe) return;
    if (!json) return;
    const focusedEl = (typeof document !== "undefined") ? document.activeElement : null;

    const namesToApply = new Set();
    pipe.resetApplySet.forEach((name) => namesToApply.add(name));
    Object.keys(pipe.adjustablesByName).forEach((name) => {
      const el = pipe.adjustablesByName[name];
      if (!el) return;
      const applyPath = el.getAttribute("data-api-sync-apply-from") || "";
      if (!applyPath.startsWith("calculated_values.")) return;
      const isDirty = (el.dataset.apiSyncDirty || "").toLowerCase() === "true";
      if (isDirty) return;
      namesToApply.add(name);
    });

    namesToApply.forEach((name) => {
      const el = pipe.adjustablesByName[name];
      if (!el) return;
      if (focusedEl && el === focusedEl) return;
      const applyPath = el.getAttribute("data-api-sync-apply-from");
      if (!applyPath) return;
      const value = this.lookup(json, applyPath);
      if (value == null || value === "") return;
      try {
        el.value = String(value);
        if (el.dataset && el.dataset.controller && el.dataset.controller.includes("number-format")) {
          el.dataset.raw = String(value);
          el.dispatchEvent(new Event("number-format:normalize", { bubbles: true }));
        }
        el.dataset.apiSyncDirty = "false";
        const inputEv = new CustomEvent("input", { bubbles: true, detail: { fromInvestApply: true } });
        inputEv.__investDetailsFromApply = true;
        el.dispatchEvent(inputEv);
        const changeEv = new CustomEvent("change", { bubbles: true, detail: { fromInvestApply: true } });
        changeEv.__investDetailsFromApply = true;
        el.dispatchEvent(changeEv);
      } catch (_) {}
    });
    pipe.resetApplySet.clear();
  }

  // ------- Helpers (mostly ported from previous controller) -------
  collectAdjustablesWithin(forms) {
    const map = {};
    const els = [];
    (forms || []).forEach((form) => {
      try { els.push(...Array.from(form.querySelectorAll("[data-api-sync-adjustable]"))); } catch (_) {}
    });
    els.forEach((el) => {
      const name = el.getAttribute("name");
      if (name) {
        map[name] = el;
        if (!el.dataset.apiSyncDirty) el.dataset.apiSyncDirty = "false";
      }
    });
    return map;
  }

  collectResetTargetsWithin(forms) {
    const targets = new Set();
    const els = [];
    (forms || []).forEach((form) => {
      try { els.push(...Array.from(form.querySelectorAll("[data-api-sync-reset-dirty-for]"))); } catch (_) {}
    });
    els.forEach((el) => {
      try {
        const list = (el.getAttribute("data-api-sync-reset-dirty-for") || "")
          .split(",")
          .map((s) => s.trim())
          .filter(Boolean);
        list.forEach((name) => targets.add(name));
      } catch (_) {
        // ignore malformed attributes
      }
    });
    return targets;
  }

  showSpinner(show, scope) {
    const sinks = this.resolveEventSinks(scope);
    const spinners = [];
    sinks.forEach((sink) => {
      try { spinners.push(...Array.from(sink.querySelectorAll("[data-api-sync-target='spinner']"))); } catch (_) {}
    });
    spinners.forEach((s) => {
      try {
        s.classList.toggle("d-none", !show);
      } catch (_) {}
    });
  }

  cancelInFlight(scope) {
    const pipe = this.pipelines.get(scope);
    if (!pipe) return;
    if (pipe.abortController) {
      try { pipe.abortController.abort(); } catch (_) {}
      pipe.abortController = null;
    }
  }

  /**
   * Transforme un <form> en objet nested (style Rails) en utilisant FormData.
   * Supporte les arrays Rails "a[b][]" (FormData: plusieurs entrées).
   */
  formToNestedObject(form) {
    const data = new FormData(form);
    const out = {};
    for (const [name, value] of data.entries()) {
      if (value === null || value === undefined || value === "") continue;
      this.assignNested(out, name, value);
    }
    return out;
  }

  /**
   * Assigne une valeur en construisant la structure nested.
   * Supports:
   * - "a[b][c]" (Rails)
   * - "a.b.c"
   * - "a[b][]" (arrays Rails)
   */
  assignNested(target, key, value) {
    // Rails arrays: a[b][] (FormData emits multiple entries with the same name).
    const isArray = /\[\]$/.test(String(key || ""));
    const cleanKey = isArray ? String(key).replace(/\[\]$/, "") : key;
    const path = this.keyToPath(cleanKey);
    let obj = target;
    for (let i = 0; i < path.length; i++) {
      const part = path[i];
      const last = i === path.length - 1;
      if (last) {
        if (isArray) {
          if (!Object.prototype.hasOwnProperty.call(obj, part) || !Array.isArray(obj[part])) {
            obj[part] = [];
          }
          obj[part].push(value);
        } else {
          obj[part] = value;
        }
      } else {
        if (!Object.prototype.hasOwnProperty.call(obj, part) || typeof obj[part] !== "object" || obj[part] === null) {
          obj[part] = {};
        }
        obj = obj[part];
      }
    }
  }

  /**
   * Convertit une clé Rails / dot en tableau de segments.
   * @param {string} key
   * @returns {string[]}
   */
  keyToPath(key) {
    if (key.indexOf("[") >= 0) {
      // Rails style a[b][c]
      return key.replace(/]/g, "").split("[").map((s) => s.trim()).filter((s) => s.length > 0);
    }
    // dot style a.b.c
    return key.split(".").map((s) => s.trim()).filter((s) => s.length > 0);
  }

  deepMerge(dst, src) {
    for (const k of Object.keys(src)) {
      if (src[k] && typeof src[k] === "object" && !Array.isArray(src[k])) {
        if (!dst[k] || typeof dst[k] !== "object") dst[k] = {};
        this.deepMerge(dst[k], src[k]);
      } else {
        dst[k] = src[k];
      }
    }
    return dst;
  }

  /**
   * Applique un patch "one-shot" au payload construit depuis les forms.
   * Ne mute jamais l'objet d'origine.
   * @param {object} basePayload
   * @param {object} payloadPatch
   * @returns {object}
   */
  applyPayloadPatch(basePayload, payloadPatch) {
    try {
      const cloned = JSON.parse(JSON.stringify(basePayload || {}));
      if (!payloadPatch || typeof payloadPatch !== "object") return cloned;
      return this.deepMerge(cloned, payloadPatch);
    } catch (_) {
      // Fallback: if cloning fails, at least try to merge into a shallow copy.
      const shallow = Object.assign({}, basePayload || {});
      if (!payloadPatch || typeof payloadPatch !== "object") return shallow;
      return this.deepMerge(shallow, payloadPatch);
    }
  }

  /**
   * Détecte si un patch contient un override de fiscalité (comparateur).
   * Ces patches doivent persister jusqu'à une réinitialisation explicite.
   * @param {object|null} patch
   * @returns {boolean}
   */
  isFiscalityPatch(patch) {
    return patch && patch.criterias && patch.criterias.tax_program;
  }

  deleteByName(obj, name) {
    if (!obj || !name) return;
    const path = this.keyToPath(name);
    let cur = obj;
    for (let i = 0; i < path.length; i++) {
      const key = path[i];
      if (i === path.length - 1) {
        if (cur && Object.prototype.hasOwnProperty.call(cur, key)) {
          delete cur[key];
        }
      } else {
        if (!cur || typeof cur[key] !== "object") return;
        cur = cur[key];
      }
    }
  }

  lookup(obj, path) {
    if (!obj || !path) return null;
    const parts = path.replace(/\[(\d+)\]/g, ".$1").split(".").filter(Boolean);
    let cur = obj;
    for (const p of parts) {
      if (cur == null) return null;
      cur = cur[p];
    }
    return cur == null ? null : cur;
  }

  // ------- Multi-scope helpers -------
  /**
   * Re-scanne les formulaires `[data-api-sync-form]` réellement présents dans
   * le DOM et rafraîchit les pipelines si la liste a changé.
   *
   * Appelé avant toute lecture des formulaires (saisie, construction du
   * payload, refresh) : le cache figé au `connect()` continuait sinon de
   * pointer sur des nœuds détachés dès qu'un sous-arbre du conteneur était
   * remplacé par `innerHTML`, d'où un formulaire neuf jamais écouté et des
   * payloads construits sur des valeurs périmées (#662).
   * @returns {boolean} true si la liste des formulaires a changé
   */
  syncForms() {
    const forms = Array.from(this.element.querySelectorAll("form[data-api-sync-form]"));
    if (this.sameForms(forms, this.allForms)) return false;
    this.allForms = forms;
    this.rebuildPipelines();
    return true;
  }

  sameForms(a, b) {
    const left = a || [];
    const right = b || [];
    return left.length === right.length && left.every((form, i) => form === right[i]);
  }

  rebuildPipelines() {
    // Determine scopes in use
    const scopesSet = new Set();
    this.allForms.forEach((form) => {
      this.parseScopes(form.getAttribute("data-api-sync-scope")).forEach((s) => scopesSet.add(s));
    });
    const scopes = this.singleScope ? [this.singleScope] : Array.from(scopesSet);

    // Pipelines dont tous les formulaires ont quitté le DOM.
    Array.from(this.pipelines.keys()).forEach((scope) => {
      if (scopes.includes(scope)) return;
      this.cancelInFlight(scope);
      const pipe = this.pipelines.get(scope);
      if (pipe && pipe.debounceTimer) clearTimeout(pipe.debounceTimer);
      this.pipelines.delete(scope);
    });

    scopes.forEach((scope) => {
      const forms = this.allForms.filter((f) => this.formHasScope(f, scope));
      const adjustablesByName = this.collectAdjustablesWithin(forms);
      const resetTargetsSet = this.collectResetTargetsWithin(forms);
      const existing = this.pipelines.get(scope);
      if (existing) {
        // Seules les données dérivées du DOM sont recalculées : l'état runtime
        // (patch sticky de fiscalité, requête en vol, debounce, champs à
        // réappliquer) doit survivre au remplacement d'un formulaire.
        const replaced = !this.sameForms(forms, existing.forms);
        existing.forms = forms;
        existing.adjustablesByName = adjustablesByName;
        existing.resetTargetsSet = resetTargetsSet;
        if (replaced) {
          // La dédup compare le payload à la dernière réponse *appliquée* ;
          // or le sous-arbre qui la portait vient d'être remplacé. On la
          // réarme pour ne pas sauter le prochain fetch (cf. issue #227).
          existing.lastRequestBody = null;
          existing.lastSuccessfulBody = null;
        }
        return;
      }
      this.pipelines.set(scope, {
        scope,
        forms,
        requestId: 0,
        abortController: null,
        debounceTimer: null,
        lastRequestBody: null,
        // Body et JSON de la dernière réponse réellement appliquée. Utilisés
        // par la logique de dedupe pour détecter et corriger une éventuelle
        // désynchronisation entre l'état du formulaire et l'UI (issue #227).
        lastSuccessfulBody: null,
        lastSuccessJson: null,
        // Compteur de fetch en vol pour ce pipeline (cf. `apiSyncBusy`).
        inFlight: 0,
        adjustablesByName,
        resetApplySet: new Set(),
        resetTargetsSet,
      });
    });
  }

  /**
   * Parse une liste de scopes CSV (ex: "a,b") en tableau.
   * @param {string} raw
   */
  parseScopes(raw) {
    return String(raw || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

  /**
   * Pipelines déclarés comme « suiveurs » de `scope` via
   * `data-api-sync-follow-scope` sur l'un de leurs formulaires. Un bloc qui a
   * son propre endpoint mais dépend des mêmes critères (ex. le comparateur
   * neuf / ancien, scope `new_vs_old`, suiveur de `listing_details`) se
   * rafraîchit ainsi sans que les contrôleurs émetteurs aient à le connaître.
   * @param {string} scope
   * @returns {string[]}
   */
  followersOf(scope) {
    const followers = [];
    this.pipelines.forEach((pipe, pipeScope) => {
      if (pipeScope === scope) return;
      const follows = pipe.forms.some((form) =>
        this.parseScopes(form.getAttribute("data-api-sync-follow-scope")).includes(scope)
      );
      if (follows) followers.push(pipeScope);
    });
    return followers;
  }

  formHasScope(form, scope) {
    if (!form || !scope) return false;
    const scopes = this.parseScopes(form.getAttribute("data-api-sync-scope"));
    return scopes.includes(scope);
  }

  /**
   * Récupère le token API depuis le meta tag injecté par le layout.
   * @returns {string|null}
   */
  getApiToken() {
    const meta = document.querySelector('meta[name="api-token"]');
    return meta ? meta.getAttribute("content") : null;
  }
}



